/**
 * WBShopifyRecommendations - "Customers also bought" from Shopify instead of
 * Tagalys' bought_also_bought (Graham 14.10.2026: Tagalys' widget takes no
 * merchandising rules, so it can't boost tops on a bottom PDP or the reverse).
 * assets/tagalys-recommendations.js hands boughtAlsoBought over to this when it
 * is loaded (layout/theme.liquid, Tagalys-reco shops), so the PDP tabs, the PDP
 * related products and the side cart all use it.
 *
 * Sources, all storefront requests (nothing private):
 * - Shopify product recommendations (/recommendations/products.json):
 *   intent=complementary = the products picked by hand in the Search & Discovery
 *   app, intent=related = Shopify's own picks from years of orders (max 10).
 * - Shopify search through templates/search.reco-tags.liquid, two calls side by
 *   side: the matching set (same range + colour, the other half of the outfit)
 *   and the other half in the same colour from every range (max 24).
 *
 * Order on sportswear tops / bottoms (custom_metafield_category_top / _bottom):
 *   1. hand-picked complementary products
 *   2. the matching set: same range and colour, the other half (max 4)
 *   3. related products from the other half (tops on a bottom, bottoms on a top)
 *   4. the other related products, at most 2 other colours of this same style
 *   5. the rest of the matching set, then the other half in the same colour
 *      from other ranges, then the remaining colours of this style
 * A product's list is kept in sessionStorage for 30 minutes, so coming back to
 * a PDP or opening the side cart for it costs no request.
 * Other products (nutrition, unitards, accessories) keep steps 1 and 4 only.
 * Shopify's picks stay within the product line (sportswear / nutrition). The
 * product itself and sold-out products are always left out; hidden and
 * preview-launch products go through WBTagalysSearchAdapter like every reco.
 */
(function () {
  var LABEL = '[WBShopifyRecommendations]';
  var CATEGORY = 'custom_metafield_category_';
  var RANGE = 'custom_metafield_range_';
  var COLOUR = 'custom_metafield_color_';
  var LINE = 'product_line_';
  var OTHER_HALF = { top: 'bottom', bottom: 'top' };
  var MATCHING_SET_FIRST = 4;
  var SAME_STYLE_FIRST = 2;
  var RELATED_MAX = 10;

  var sources = {};
  var results = {};

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getJson(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (response) {
      if (!response.ok) {
        console.error(LABEL, 'HTTP error', url, response.status);
        return null;
      }
      return response.json();
    }).catch(function (error) {
      console.error(LABEL, url, error);
      return null;
    });
  }

  function tagValue(tags, prefix) {
    var tag = (tags || []).filter(function (item) { return String(item).indexOf(prefix) === 0; })[0];
    return tag ? String(tag).slice(prefix.length) : '';
  }

  function hasTag(product, tag) {
    return (product.tags || []).indexOf(tag) !== -1;
  }

  // "Emphasize Mid Waist Leggings | Black" -> "emphasize mid waist leggings"
  function styleName(title) {
    return String(title || '').split('|')[0].trim().toLowerCase();
  }

  // The product the panel is for (its tags give category, range and colour):
  // the PDP's product (window.wbPdpProduct, layout/theme.liquid - no request),
  // else the cart line with that product (side cart on other pages), else the
  // product in the URL. null when none matches - the ranking then keeps
  // Shopify's order.
  function sourceProduct(productId) {
    var id = String(productId);
    if (sources[id]) return sources[id];
    var pdp = window.wbPdpProduct;
    if (pdp && String(pdp.id) === id) {
      sources[id] = Promise.resolve(pdp);
      return sources[id];
    }
    var handles = [];
    // The Vuex store is window.vueapp.$store (snippets/vue-init.liquid).
    var store = (window.vueapp && window.vueapp.$store) || window.vuestore;
    var cart = store && store.state && store.state.cart;
    ((cart && cart.items) || []).forEach(function (item) {
      if (String(item.product_id) === id && item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });
    var match = /\/products\/([^\/?#]+)/.exec(window.location.pathname);
    if (!handles.length && match) handles.push(decodeURIComponent(match[1]));
    sources[id] = handles.reduce(function (found, handle) {
      return found.then(function (product) {
        if (product) return product;
        return getJson(root() + 'products/' + encodeURIComponent(handle) + '.js').then(function (data) {
          return data && String(data.id) === id ? data : null;
        });
      });
    }, Promise.resolve(null));
    return sources[id];
  }

  // Shopify's recommendations JSON has prices in cents and image URLs as plain
  // strings; bring it to the v2 product shape the adapter reads (same as
  // snippets/athlete-search.liquid).
  function fromRecommendation(product) {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      tags: product.tags || [],
      available: !!product.available,
      price: (product.price || 0) / 100,
      compare_at_price: (product.compare_at_price || 0) / 100,
      compare_at_price_max: (product.compare_at_price_max || 0) / 100,
      featured_image: { src: product.featured_image || '' },
      images: (product.images || []).map(function (src) { return { src: src }; }),
      variants: (product.variants || []).map(function (variant) {
        return {
          id: variant.id,
          title: variant.title,
          sku: variant.sku,
          available: !!variant.available,
          price: (variant.price || 0) / 100,
          compare_at_price: (variant.compare_at_price || 0) / 100
        };
      })
    };
  }

  function recommendations(productId, intent) {
    var url = root() + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + RELATED_MAX + '&intent=' + intent;
    return getJson(url).then(function (data) {
      return data && Array.isArray(data.products) ? data.products.map(fromRecommendation) : [];
    });
  }

  // Products carrying all the tags, already in the v2 shape (search view).
  function productsWithAllTags(tags) {
    var q = tags.map(function (tag) { return 'tag:' + tag; }).join(' AND ');
    return getJson(root() + 'search?view=reco-tags&type=product&q=' + encodeURIComponent(q)).then(function (data) {
      return data && Array.isArray(data.products) ? data.products : [];
    });
  }

  function rank(source, productId, lists) {
    var complementary = lists[0] || [];
    var related = lists[1] || [];
    var matchingSet = lists[2] || [];
    var sameColour = lists[3] || [];
    var tags = (source && source.tags) || [];
    var otherHalf = OTHER_HALF[tagValue(tags, CATEGORY)];
    var style = source ? styleName(source.title) : '';
    // Shopify's picks stay within the product line (no gummies on a leggings PDP);
    // the hand-picked products are shown as picked.
    // A product can carry several line tags (product_line_beauty + product_line_fitness),
    // so a pick stays when it shares any of them.
    var lines = tags.filter(function (tag) { return String(tag).indexOf(LINE) === 0; });
    if (lines.length) {
      related = related.filter(function (product) {
        return lines.some(function (line) { return hasTag(product, line); });
      });
    }
    var seen = {};
    var ranked = [];
    var laterColours = [];
    var styleCount = 0;
    seen[String(productId)] = true;

    function add(product) {
      var id = String(product.id);
      if (seen[id] || !product.available) return;
      seen[id] = true;
      ranked.push(product);
    }
    function addRelated(product) {
      if (style && styleName(product.title) === style && !seen[String(product.id)]) {
        styleCount++;
        if (styleCount > SAME_STYLE_FIRST) {
          laterColours.push(product);
          return;
        }
      }
      add(product);
    }

    complementary.forEach(add);
    if (otherHalf) {
      // Matching pieces Shopify also relates to this product go first.
      var relatedIds = related.map(function (product) { return String(product.id); });
      matchingSet = matchingSet.slice().sort(function (a, b) {
        var ia = relatedIds.indexOf(String(a.id));
        var ib = relatedIds.indexOf(String(b.id));
        return (ia === -1 ? RELATED_MAX : ia) - (ib === -1 ? RELATED_MAX : ib);
      });
      matchingSet.slice(0, MATCHING_SET_FIRST).forEach(add);
      related.filter(function (product) { return hasTag(product, CATEGORY + otherHalf); }).forEach(add);
      related.forEach(addRelated);
      matchingSet.forEach(add);
      sameColour.forEach(add);
    } else {
      related.forEach(addRelated);
    }
    laterColours.forEach(add);
    return ranked;
  }

  var CACHE_PREFIX = 'wbAlsoBought:';
  var CACHE_MS = 30 * 60 * 1000;

  function cacheRead(key) {
    try {
      var entry = JSON.parse(window.sessionStorage.getItem(CACHE_PREFIX + key));
      if (entry && entry.at && Date.now() - entry.at < CACHE_MS && Array.isArray(entry.products)) return entry.products;
    } catch (error) { /* storage blocked or corrupt entry: fetch instead */ }
    return null;
  }

  function cacheWrite(key, products) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ at: Date.now(), products: products }));
    } catch (error) { /* storage full or blocked: nothing to do */ }
  }

  // Same contract as WBTagalysRecommendations.boughtAlsoBought: resolves to
  // render-ready hits, [] on failure. One lookup per product and limit - the PDP
  // tabs and the side cart ask for the same product more than once.
  function alsoBought(productId, limit, isNutrition) {
    if (!productId) return Promise.resolve([]);
    limit = limit || 12;
    // The locale root is part of the key: /es and /en on the same shop have their own titles.
    var key = root() + ':' + String(productId) + ':' + limit + ':' + (isNutrition ? 'n' : 'f');
    if (results[key]) return results[key];
    var cached = cacheRead(key);
    if (cached) {
      results[key] = Promise.resolve(window.WBTagalysSearchAdapter.fromProducts({ products: cached }).slice(0, limit));
      return results[key];
    }
    // Shopify's picks need no product data: they run while the product loads;
    // the tag searches start as soon as the product's tags are known.
    var picks = Promise.all([recommendations(productId, 'complementary'), recommendations(productId, 'related')]);
    results[key] = sourceProduct(productId).then(function (source) {
      var tags = (source && source.tags) || [];
      var otherHalf = OTHER_HALF[tagValue(tags, CATEGORY)];
      var colour = tagValue(tags, COLOUR);
      var range = tagValue(tags, RANGE);
      var pairs = !isNutrition && otherHalf && colour;
      // Two searches side by side: the matching set on its own (the same-colour
      // list is capped at 24 and black alone has 81 tops) and the same-colour
      // other half to fill up with.
      var matchingSet = pairs && range ? productsWithAllTags([RANGE + range, COLOUR + colour, CATEGORY + otherHalf]) : Promise.resolve([]);
      var sameColour = pairs ? productsWithAllTags([COLOUR + colour, CATEGORY + otherHalf]) : Promise.resolve([]);
      return Promise.all([picks, matchingSet, sameColour]).then(function (loaded) {
        var products = rank(source, productId, loaded[0].concat([loaded[1], loaded[2]])).slice(0, limit);
        cacheWrite(key, products);
        return products;
      });
    }).then(function (products) {
      return window.WBTagalysSearchAdapter.fromProducts({ products: products }).slice(0, limit);
    }).catch(function (error) {
      console.error(LABEL, error);
      delete results[key];
      return [];
    });
    return results[key];
  }

  // ---- "Recommendations" tab: best sellers from Shopify sales
  // (Graham 15.10.2026: instead of the same Tagalys best sellers on every PDP).
  // Order = the collection's sort_by=best-selling through templates/collection.reco-json.liquid:
  //   1. tops / bottoms: the other half's best sellers from every range (Graham 16.10.2026: tops with bottoms,
  //      bottoms with tops, no need to match the range), at most 2 colours per style first.
  //      Other pieces (unitards): the range's pieces of the same product type, 2 colours per style first,
  //      then the other colours of that type
  //   2. the rest of the range (not for tops / bottoms), at most 2 colours per style first
  //   3. filled up with the line's best sellers (sportswear pieces with a category; nutrition products)
  //   4. then the remaining colours of the range
  // In stock only, never the product itself. The PDP tab leaves out what "Customers also bought" shows.
  var BEST_PREFIX = 'wbRangeBest4:';
  var LINE_COLLECTION = { fashion: 'shop-sportswear', fitness: 'shop-fitness' };

  // "Sportswear/Legging/", "Sportswear/Leggings/" -> "sportswear/legging" (the types are not written the same everywhere),
  // with the category: unitards and jackets are both "Sportswear/Hoodies/" (category unitard / top)
  function productType(product) {
    var type = String((product && product.type) || '').toLowerCase().replace(/^\/+|\/+$/g, '').replace(/s$/, '');
    return type ? type + '|' + tagValue(product.tags, CATEGORY) : '';
  }

  // Plain fetch: with an Accept: application/json header Shopify answers collection URLs with its own
  // collection JSON and ignores ?view= (same as the ME search index, see a6ed07f84).
  var bestSellingRequests = {};
  function bestSelling(handle, tag) {
    var k = handle + '/' + (tag || '');
    if (!bestSellingRequests[k]) bestSellingRequests[k] = bestSellingFetch(handle, tag);
    return bestSellingRequests[k];
  }

  function bestSellingFetch(handle, tag) {
    var url = root() + 'collections/' + handle + (tag ? '/' + encodeURIComponent(tag) : '') + '?view=reco-json&sort_by=best-selling';
    return fetch(url).then(function (response) {
      if (!response.ok) {
        console.error(LABEL, 'HTTP error', url, response.status);
        return null;
      }
      return response.json();
    }).catch(function (error) {
      console.error(LABEL, url, error);
      return null;
    }).then(function (data) {
      return data && Array.isArray(data.products) ? data.products : [];
    });
  }

  function rangeBestSellers(productId, limit, isNutrition) {
    if (!productId) return Promise.resolve([]);
    limit = limit || 12;
    var key = root() + ':' + String(productId) + ':' + limit + ':' + (isNutrition ? 'n' : 'f');
    if (results[BEST_PREFIX + key]) return results[BEST_PREFIX + key];
    var cached = cacheRead(BEST_PREFIX + key);
    if (cached) {
      results[BEST_PREFIX + key] = Promise.resolve(window.WBTagalysSearchAdapter.fromProducts({ products: cached }).slice(0, limit));
      return results[BEST_PREFIX + key];
    }
    var lineHandle = LINE_COLLECTION[isNutrition ? 'fitness' : 'fashion'];
    var lineList = bestSelling(lineHandle, null);
    var sourceType = '';
    results[BEST_PREFIX + key] = sourceProduct(productId).then(function (source) {
      var tags = (source && source.tags) || [];
      sourceType = isNutrition ? '' : productType(source);
      var range = isNutrition ? '' : tagValue(tags, RANGE);
      var otherHalf = isNutrition ? '' : OTHER_HALF[tagValue(tags, CATEGORY)];
      if (otherHalf) {
        sourceType = '';
        return Promise.all([bestSelling(lineHandle, CATEGORY + otherHalf), lineList]);
      }
      return Promise.all([range ? bestSelling(lineHandle, RANGE + range) : Promise.resolve([]), lineList]);
    }).then(function (lists) {
      var seen = {};
      seen[String(productId)] = true;
      var out = [];
      var later = [];
      var perStyle = {};
      function add(p) {
        var id = String(p.id);
        if (seen[id] || !p.available) return;
        seen[id] = true;
        out.push(p);
      }
      // only products that will really show count towards the 2 colours per style
      function addFewColours(p, overflow) {
        if (seen[String(p.id)] || !p.available) return;
        var style = styleName(p.title);
        if ((perStyle[style] || 0) >= 2) { if (overflow) overflow.push(p); return; }
        perStyle[style] = (perStyle[style] || 0) + 1;
        add(p);
      }
      if (sourceType) {
        // the PDP's type: 2 colours per style first (several leggings styles on Power), then its other colours (Define has one)
        var sameType = lists[0].filter(function (p) { return productType(p) === sourceType; });
        sameType.forEach(function (p) { addFewColours(p, null); });
        sameType.forEach(add);
      }
      lists[0].forEach(function (p) { addFewColours(p, later); });
      lists[1].forEach(function (p) {
        // the line's best sellers include resistance bands and socks: only real pieces (sportswear) / nutrition
        var real = isNutrition ? !(p.tags || []).some(function (t) { return t === 'accessories' || t === 'bundle'; }) : !!tagValue(p.tags, CATEGORY);
        if (real) add(p);
      });
      later.forEach(add);
      var products = out.slice(0, limit);
      // an empty list (e.g. both requests failed) is not kept, the next page view tries again
      if (products.length) cacheWrite(BEST_PREFIX + key, products);
      return window.WBTagalysSearchAdapter.fromProducts({ products: products }).slice(0, limit);
    }).catch(function (error) {
      console.error(LABEL, error);
      delete results[BEST_PREFIX + key];
      return [];
    });
    return results[BEST_PREFIX + key];
  }

  // Simple fallback for the "Recommendations" tab when the range list is too short: the line's
  // best sellers (sportswear pieces / nutrition), in stock, only the product itself left out.
  function lineBestSellers(productId, limit, isNutrition) {
    limit = limit || 12;
    return bestSelling(LINE_COLLECTION[isNutrition ? 'fitness' : 'fashion'], null).then(function (list) {
      var products = list.filter(function (p) {
        if (String(p.id) === String(productId) || !p.available) return false;
        return isNutrition ? !(p.tags || []).some(function (t) { return t === 'accessories' || t === 'bundle'; }) : !!tagValue(p.tags, CATEGORY);
      }).slice(0, limit);
      return window.WBTagalysSearchAdapter.fromProducts({ products: products }).slice(0, limit);
    }).catch(function (error) {
      console.error(LABEL, error);
      return [];
    });
  }

  window.WBShopifyRecommendations = {
    alsoBought: alsoBought,
    rangeBestSellers: rangeBestSellers,
    lineBestSellers: lineBestSellers
  };
})();
